// =============================================================================
// legal-client.http.ts — Adaptateur HTTP RÉEL du LegalClient (CGU fournisseur, D-LEGAL-API).
// =============================================================================
// Remplace le stub-first (Option B) derrière le MÊME DI seam (LegalClientProvider) : les hooks/écrans
// consomment l'interface LegalClient, l'impl est substituée sans toucher l'UI. Miroir des routes
// FOURNISSEUR de legal.controller.ts (@Controller('legal/cgu'), routes @SupplierRoute).
//
// AUTORITÉ = API : la valeur juridique de la signature (CguAcceptance immuable, DE.CguAcceptanceRecorded
// legally-binding) est l'API. Le siret/contact signataire viennent de la SESSION (jamais du body,
// invariant n°1) : l'UI n'envoie que `{cguVersionId, contentDisplayed}`. `contentDisplayed=true` n'est
// posé par l'UI qu'après scroll complet + acceptation explicite (EF-LEG-3) — cet adaptateur relaie.
//
// SÉCURITÉ (RULE_REF_006) : la session FOURNISSEUR est portée par le COOKIE httpOnly `supplier_session`
// posé par l'API (`credentials: 'include'`) — jamais lu/fabriqué ici.
//
// CSRF DOUBLE-SUBMIT FOURNISSEUR : sur les mutations (POST acceptances), l'en-tête `x-csrf-token` DOIT
// égaler le cookie NON-httpOnly `supplier_csrf` (supplier-cookies.ts SUPPLIER_CSRF_COOKIE).
// ⚠️ DIVERGENCE ASSUMÉE vs admin : le cookie CSRF FOURNISSEUR est `supplier_csrf` (admin = `admin_csrf`).
// Même header, même patron double-submit ; SEUL le nom de cookie change (parité HttpRateCollectionClient).
// Sans l'en-tête, toute écriture fournisseur est 403 (fail-closed serveur). Les GET ne portent pas de CSRF.
//
// PDF (currentCguPdf) : l'endpoint /legal/cgu/current/pdf streame les OCTETS (application/pdf). On lit
// donc `response.arrayBuffer()` (PAS `.json()`) ; 404 (NoPublishedCguError) → LegalApiError.

import {
  LegalApiError,
  type AcceptCguInput,
  type CguAcceptanceResult,
  type CguChangelogView,
  type CguVersionView,
  type HasSignedView,
  type LegalClient,
} from '@/api/legal-client';

/** Forme attendue d'un corps d'erreur structuré (NestJS HttpException `{ statusCode, message }`). */
interface StructuredErrorBody {
  readonly statusCode?: number;
  readonly message?: string;
}

/** Nom du cookie CSRF NON-httpOnly FOURNISSEUR (cf. API supplier-cookies.ts SUPPLIER_CSRF_COOKIE). */
const SUPPLIER_CSRF_COOKIE_NAME = 'supplier_csrf';

/**
 * Lit la valeur du cookie CSRF fournisseur `supplier_csrf` (NON-httpOnly, lisible par le SPA même
 * origine). `undefined` si absent (pas de session, ou contexte sans `document`) — l'appel mutant
 * partira alors sans en-tête et l'API répondra 403 (fail-closed serveur, jamais d'écriture).
 * Parité stricte avec HttpRateCollectionClient (rate-collection-client.http.ts).
 */
function readSupplierCsrfToken(): string | undefined {
  if (typeof document === 'undefined') {
    return undefined;
  }
  for (const part of document.cookie.split(';')) {
    const [rawName, ...rest] = part.trim().split('=');
    if (rawName === SUPPLIER_CSRF_COOKIE_NAME) {
      return decodeURIComponent(rest.join('='));
    }
  }
  return undefined;
}

/**
 * Adaptateur HTTP réel : `fetch` contre l'API fournisseur legal. `baseUrl` par défaut vide (chemins
 * relatifs : SPA et API partagent l'origine, ou un reverse proxy route `/legal/*`). En test, on
 * injecte un baseUrl ou on mocke `fetch`.
 */
export class HttpLegalClient implements LegalClient {
  public constructor(private readonly baseUrl: string = '/api') {}

  /** Construit l'URL absolue (baseUrl + chemin relatif racine API). */
  private url(path: string): string {
    return `${this.baseUrl}${path}`;
  }

  /**
   * Exécute une requête et renvoie la Response brute (OK garanti). Sur les méthodes MUTANTES (POST),
   * pose l'en-tête CSRF `x-csrf-token` = cookie `supplier_csrf`. En cas d'erreur HTTP, lève une
   * LegalApiError (message serveur relayé tel quel). Le parse (JSON vs octets) est laissé à l'appelant.
   */
  private async fetchOk(path: string, init: RequestInit): Promise<Response> {
    const method = (init.method ?? 'GET').toUpperCase();
    const headers: Record<string, string> = { ...(init.headers as Record<string, string>) };
    if (method !== 'GET' && method !== 'HEAD') {
      const csrf = readSupplierCsrfToken();
      if (csrf !== undefined) {
        headers['x-csrf-token'] = csrf;
      }
    }

    const response = await fetch(this.url(path), {
      // Session fournisseur = cookie httpOnly `supplier_session` : transmis avec credentials.
      credentials: 'include',
      ...init,
      headers,
    });

    if (!response.ok) {
      throw await this.toApiError(response);
    }
    return response;
  }

  /** GET fournisseur (cookie de session) → JSON typé, ou lève une erreur mappée. */
  private async getJson<T>(path: string): Promise<T> {
    const response = await this.fetchOk(path, { method: 'GET' });
    return (await response.json()) as T;
  }

  /** Convertit une réponse non-OK en LegalApiError typée (message serveur tel quel). */
  private async toApiError(response: Response): Promise<LegalApiError> {
    let body: StructuredErrorBody | undefined;
    try {
      body = (await response.json()) as StructuredErrorBody;
    } catch {
      body = undefined;
    }
    const message =
      body?.message ?? `Erreur API (${String(response.status)}) sur les CGU.`;
    // Le `status` est CONSERVÉ : la présentation distingue 404 (aucune CGU publiée,
    // NoPublishedCguError) des autres échecs, sans réinterpréter le message métier.
    return new LegalApiError(message, response.status);
  }

  public currentCgu(): Promise<CguVersionView> {
    return this.getJson<CguVersionView>('/legal/cgu/current');
  }

  public async currentCguPdf(): Promise<ArrayBuffer> {
    // OCTETS (application/pdf) : on lit arrayBuffer(), PAS json(). 404 si aucune version publiée.
    const response = await this.fetchOk('/legal/cgu/current/pdf', { method: 'GET' });
    return response.arrayBuffer();
  }

  public hasSigned(): Promise<HasSignedView> {
    return this.getJson<HasSignedView>('/legal/cgu/has-signed');
  }

  public currentCguChangelog(): Promise<CguChangelogView> {
    return this.getJson<CguChangelogView>('/legal/cgu/current/changelog');
  }

  public async acceptCgu(input: AcceptCguInput): Promise<CguAcceptanceResult> {
    // Le siret/contact signataire viennent de la SESSION côté API (jamais du body, invariant n°1).
    const response = await this.fetchOk('/legal/cgu/acceptances', {
      method: 'POST',
      body: JSON.stringify(input),
      headers: { 'content-type': 'application/json' },
    });
    return (await response.json()) as CguAcceptanceResult;
  }
}
